import React, { useState, Fragment, useEffect } from "react";
import { Link, withRouter, RouteComponentProps } from "react-router-dom";
import { UtilityFunctions } from "../../utils/UtilityFunctions";
import { STRING_CONSTANTS } from "../../utils/constants/stringConstants";
import { Helmet } from 'react-helmet';
import { userService, cookieService } from "../../_services";
import { useWhitelabel } from "../../context/WhitelabelContext"; // Import our new hook! Adjust path as needed

// Define props type including router props
interface ARAffSignupProps extends RouteComponentProps<any> {
  nextButton: () => void;
}

const ARAffSignup: React.FC<ARAffSignupProps> = (props) => {
  // --- State Management ---
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [affiliateCode, setAffiliateCode] = useState(localStorage.getItem("referralCode") || '');
  const [pwdType, setPwdType] = useState("password");
  const [btnClick, setBtnClick] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [mobileNumber, setMobileNumber] = useState('');
  const [whitelabelDomain, setWhitelabelDomain] = useState<string>('');
  
  // 🎯 NEW: State for dynamic page content
  const [pageContent, setPageContent] = useState<any>(null);
  const [isContentLoading, setIsContentLoading] = useState(true);

  // --- Detect Whitelabel Domain & Fetch Content ---
  useEffect(() => {
    const hostname = window.location.hostname;
    setWhitelabelDomain(hostname);

    const fetchContent = async (currentDomain: string) => {
      try {
        const response = await userService.aiRequest({
          method: 'get-page-content',
          data: { whitelabel_domain: currentDomain, page_name: 'signup' }
        }, false); // No auth needed for this public page

        if (response.data?.content) {
          setPageContent(response.data.content);

          // 🎯 Inject custom JS if it exists
          if (response.data.customJs) {
            const script = document.createElement('script');
            script.innerHTML = response.data.customJs;
            script.id = 'whitelabel-custom-js';
            document.body.appendChild(script);
          }
        }
      } catch (error) {
        console.error("Failed to load page content", error);
      } finally {
        setIsContentLoading(false);
      }
    };

    if (hostname) {
      fetchContent(hostname);
    }

    // Cleanup function to remove script on component unmount
    return () => {
      const existingScript = document.getElementById('whitelabel-custom-js');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  // --- Validation ---
  const hasNumber = /\d/;
  const isEmailValid = UtilityFunctions.validateEmail(email);
  const isPasswordValid = password.length >= 8 && hasNumber.test(password) && !UtilityFunctions.checkSpaceInPassword(password);
  const isMobileValid = !UtilityFunctions.isEmptyOrNullOrUndefined(mobileNumber);
  const isFormValid = isEmailValid && isPasswordValid && isMobileValid;

  // --- Handlers ---
  const togglePassword = () => setPwdType(pwdType === "password" ? "text" : "password");
  const { logoSrc } = useWhitelabel();

  const handleSubmit = async (event: React.FormEvent) => {
    // ... (Your existing handleSubmit logic remains exactly the same)
    event.preventDefault();
    setBtnClick(true);
    setApiError(null);
  
    if (!isFormValid) return;
  
    setIsLoading(true);
    try {
      const signupData = { email, password, mobileNumber, type: 'Whitelabel-er', whitelabelDomain, isAccountsSignup: true };
      await userService.signup(signupData, affiliateCode, true);
      const loginResponse = await userService.login({ email, password });
      
      if (loginResponse.data?.user?.token) {
        localStorage.setItem('access_token', loginResponse.data.user.token.access);
        localStorage.setItem('refresh_token', loginResponse.data.user.token.refresh);
        localStorage.setItem('user_data', JSON.stringify(loginResponse.data.user));
        localStorage.setItem('isGuest', 'false');
        
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.token, loginResponse.data.user.token.access);
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, loginResponse.data.user);
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.isGuest, false);

        await userService.getUserData();
        props.history.push('/ar');
      }
    } catch (error: any) {
      setApiError(error[0] === 'Email' ? "Email already exists" : "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Navigation Data & Styles ---
  const terms = { pathname: `/terms`, state: {} };
  const privacy = { pathname: `/privacy`, state: {} };
  const onBoarding = { pathname: "/", state: { activeIndex: 2 } };
  const styles: { [key: string]: React.CSSProperties } = { /* ... Your existing styles object ... */ 
    container: { fontFamily: "'DM Sans', sans-serif", backgroundColor: "#FFFFFF", minHeight: "100vh", color: "#242424" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "25px 50px" },
    logo: { cursor: "pointer", height: "40px" },
    socialIcons: { display: "flex" },
    divider: { opacity: 0.44, width: "100%", background: "linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 50%, rgba(0, 0, 0, 0) 100%)", height: "1px", margin: "10px 0 30px" },
    mainContent: { maxWidth: "1200px", margin: "0 auto", padding: "0 20px" },
    heroSection: { textAlign: "center", marginBottom: "50px" },
    title: { fontFamily: "'Urbanist', sans-serif", fontWeight: 700, fontSize: "48px", lineHeight: 1.2, margin: "44px 0 30px", color: "#141414" },
    subtitle: { fontFamily: "'Urbanist', sans-serif", fontSize: "20px", color: "#555555", marginTop: "20px" },
    description: { fontSize: "16px", lineHeight: 1.5, color: "#555555", margin: "15px auto", maxWidth: "560px" },
    formSection: { display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "60px" },
    formContainer: { background: "#FFFFFF", borderRadius: "12px", padding: "40px", width: "100%", maxWidth: "500px", margin: "0 auto", border: "1px solid #E0E0E0", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" },
    formTitle: { color: "#0065FF", fontSize: "24px", fontWeight: 700, marginBottom: "20px", textAlign: "center" },
    formGroup: { marginBottom: "20px", position: "relative" },
    input: { width: "100%", padding: "15px 45px 15px 15px", backgroundColor: "#F8F9FA", border: "1px solid #D1D1D1", borderRadius: "8px", color: "#242424", fontSize: "16px", outline: "none", transition: "border-color 0.3s ease" },
    inputError: { borderColor: "#dc3545" },
    inputIcon: { position: "absolute", right: "15px", top: "50%", transform: "translateY(-50%)", cursor: "pointer" },
    validationList: { listStyle: "none", padding: "0", margin: "15px 0" },
    validationItem: { display: "flex", alignItems: "center", marginBottom: "8px", fontSize: "14px", color: "#555555" },
    validSuccess: { color: "#28a745" },
    validError: { color: "#dc3545" },
    button: { background: "#0065FF", borderRadius: "8px", border: "none", boxShadow: "0px 10px 20px -10px rgba(0, 101, 255, 0.4)", padding: "16px 32px", fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "18px", color: "#FFFFFF", textDecoration: "none", display: "inline-block", margin: "20px 0", transition: "all 0.3s ease", cursor: "pointer", width: "100%" },
    buttonDisabled: { opacity: 0.6, cursor: "not-allowed" },
    featuresSection: { marginBottom: "60px" },
    footer: { background: "#F8F9FA", padding: "40px 30px 60px", textAlign: "center", borderTop: "1px solid #E0E0E0" },
  };

  if (isContentLoading) {
    return <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}><div className="spinner-border text-primary" role="status"></div></div>;
  }
  
  return (
    <Fragment>
      <Helmet>
        <title>{pageContent?.hero?.title || 'AI-Powered Invoice Collection'}</title>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Urbanist:wght@400;500;700&display=swap" rel="stylesheet" />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </Helmet>
      
      <div style={styles.container}>
        <header style={styles.header}>
          {logoSrc && <img src={logoSrc} style={{ width: '80px', display: 'block', margin: '0 auto' }}  alt="logo" />}
        </header>
        
        <div style={styles.divider}></div>
        
        <main style={styles.mainContent}>
          <section style={styles.heroSection}>
            <h1 style={styles.title}>{pageContent?.hero?.title || "Boost Your Cash Flow with AI-Powered Invoice Collection"}</h1>
            <p style={styles.subtitle}>{pageContent?.hero?.subtitle || "Let our AI handle your invoice chaser"}</p>
            <p style={styles.description}>{pageContent?.hero?.description || "Our intelligent AI system calls your customers..."}</p>
          </section>
          
          <section style={styles.formSection}>
            <div style={styles.formContainer}>
              <h2 style={styles.formTitle}>{pageContent?.form?.title || "Create Your Account"}</h2>
              
              <form onSubmit={handleSubmit} noValidate>
                {/* Email, Mobile, Password inputs remain the same */}
                <div style={styles.formGroup}>
                  <input type="email" placeholder="Email Address" style={{...styles.input, ...(btnClick && !isEmailValid ? styles.inputError : {})}} value={email} onChange={(e) => { setEmail(e.target.value); setBtnClick(false); setApiError(null); }} disabled={isLoading} required />
                  <div style={styles.inputIcon}><img src={UtilityFunctions.getImageURL() + "mail.svg"} alt="" /></div>
                </div>
                <div style={styles.formGroup}>
                  <input type="tel" placeholder="Mobile Phone" style={{...styles.input, ...(btnClick && !isMobileValid ? styles.inputError : {})}} value={mobileNumber} onChange={(e) => { setMobileNumber(e.target.value); setBtnClick(false); setApiError(null); }} disabled={isLoading} required />
                  <div style={styles.inputIcon}><img src={UtilityFunctions.getImageURL() + "phone.svg"} alt="" /></div>
                </div>
                <div style={styles.formGroup}>
                  <input type={pwdType} placeholder="Password" style={{...styles.input, ...(btnClick && !isPasswordValid ? styles.inputError : {})}} value={password} onChange={(e) => { setPassword(e.target.value); setBtnClick(false); setApiError(null); }} disabled={isLoading} required />
                  <div style={{...styles.inputIcon, cursor: 'pointer'}} onClick={togglePassword}><img src={pwdType === "password" ? UtilityFunctions.getImageURL() + 'eyeOff.png' : UtilityFunctions.getImageURL() + 'eyeOn.png'} alt="Toggle visibility" /></div>
                </div>

                <ul style={styles.validationList}>
                  <li style={{...styles.validationItem, ...(password.length <= 0 ? {} : password.length >= 8 && !UtilityFunctions.checkSpaceInPassword(password) ? styles.validSuccess : styles.validError)}}>✓ {STRING_CONSTANTS.validationString.min_chars}</li>
                  <li style={{...styles.validationItem, ...(password.length <= 0 ? {} : hasNumber.test(password) ? styles.validSuccess : styles.validError)}}>✓ {STRING_CONSTANTS.validationString.number_req}</li>
                </ul>
                
                {apiError && <div style={{ color: "#ff4757", marginBottom: "15px", padding: "10px", backgroundColor: "rgba(255, 71, 87, 0.1)", borderRadius: "4px" }}>{apiError}</div>}
                
                <button type="submit" style={{...styles.button, ...((!isFormValid || isLoading) ? styles.buttonDisabled : {})}} disabled={!isFormValid || isLoading}>
                  {isLoading ? "Creating Account..." : (pageContent?.form?.buttonText || "UNLOCK BETTER CASH FLOW NOW")}
                </button>
                
                <p style={{ fontSize: "14px", textAlign: "center" }}>
                By signing up, you agree to our{" "}
                <Link to={pageContent?.form?.termsUrl || '/terms'} style={{ color: "#0065FF" }}>Terms of Service</Link>{" "}
                and{" "}
                <Link to={pageContent?.form?.privacyUrl || '/privacy'} style={{ color: "#0065FF" }}>Privacy Policy</Link>
              </p>
                <div style={{ textAlign: "center", marginTop: "20px" }}><p>{pageContent?.form?.loginPrompt || "Already have an account?"} <Link to={STRING_CONSTANTS.navigationString.login} style={{ color: "#0065FF" }}>Login</Link></p></div>
              </form>
            </div>
          </section>

          <section style={styles.featuresSection}>
            <h2 style={{ fontFamily: "'Urbanist', sans-serif", fontWeight: 700, fontSize: "32px", textAlign: "center", marginBottom: "30px" }}>
              {pageContent?.features?.title || "Why Choose our Invoice Chaser?"}
            </h2>
            <div style={{ color: "#000", borderRadius: "12px", padding: "30px", border: "1px solid rgba(6, 239, 197, 0.2)", marginBottom: "40px" }}>
              {(pageContent?.features?.benefits || []).map((benefit, index) => (
                <div key={index} style={{ display: "flex", alignItems: "flex-start", marginBottom: "15px" }}>
                  <div style={{ color: "#28a745", marginRight: "15px", fontSize: "20px", fontWeight: "bold" }}>✓</div> 
                  <div style={{ fontSize: "16px", lineHeight: "1.5" }}>
                    <strong>{benefit.title}:</strong> {benefit.text}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </main>
        
        <footer style={styles.footer}>
          <p style={{ marginTop: "30px", fontSize: "12px", color: "#7d7d7d" }}>
            All rights reserved. Copyright © {new Date().getFullYear()}
          </p>
        </footer>
      </div>
    </Fragment>
  );
};

export default withRouter(ARAffSignup);